import { useMemo } from 'react';
import orderBy from 'lodash/orderBy';

import { IEquipment } from '@r1-frontend/api-domru/full-buy/v1/equipment/dto/Equipment.dto';
import { ICreditCondition } from '@r1-frontend/api-domru/payments/v1/credits/condition';

import format from '@r1-frontend/ui-react/components/costs/Cost/format';
import { IOptionVariant, TOption } from '@r1-frontend/ui-react/components/OptionList';
import { ICustomOptions } from '@r1-frontend/ui-react/components/OptionList/CustomOptionList';
import { RubleSymbol } from '@r1-frontend/ui-react/tokens/symbols';
import { getPluralize } from '@r1-frontend/shared/helpers/pluralize';
import { dayPluralize } from '@r1-frontend/shared/helpers/pluralize/predifined';
import { EPriceTypes } from '@r1-frontend/shared/types/EPriceTypes';

import { getPriceOptions } from '~/src/entities/equipment';
import { convertShopPrices, EPriceOperationTypes, IPriceOptionProps } from '~/src/entities/price';
import { isCreditAbnEnabled } from '~/src/features/work-in-home-ab/components/Description/components/Credit/constants/abnVariants';
import { getPayOptions } from '~/src/features/work-in-home-ab/components/Description/functions';
import { sortFunctionWithRequestIdOnTop } from '~/src/helpers/functions';

const pluralizeDays = getPluralize(dayPluralize);

const getOptionLabel = (type: number, duration: number) => {
    return type === EPriceTypes.Leasing ? `Рассрочка на ${duration} мес.` : 'Полная стоимость';
};

export const usePriceOptions = (equipment: IEquipment, creditConditions?: ICreditCondition[]) => {
    const priceOptions = useMemo<IPriceOptionProps[]>(() => {
        const basePrices = getPriceOptions(
            convertShopPrices(equipment),
            null,
            false,
            (a, b) => sortFunctionWithRequestIdOnTop(a, b),
        );

        const fullPriceOption = basePrices.find(option => option.operType === EPriceOperationTypes.Possess);

        if (isCreditAbnEnabled() && equipment.isLoanAvailable && creditConditions && creditConditions.length > 0) {
            const creditOptions = creditConditions?.map(condition => ({
                id: `credit_${condition.duration}`,
                value: condition.duration,
                label: `Рассрочка на ${condition.duration} мес.`,
                price: condition.pay,
                type: EPriceTypes.Leasing,
                operType: EPriceOperationTypes.Leasing,
                sort: 999,
            }));

            return [...creditOptions, fullPriceOption];
        }

        return basePrices;
    }, [equipment, creditConditions]);

    const payOptions = useMemo<TOption[]>(() => getPayOptions(priceOptions), [priceOptions]);

    const testDriveOptions: (ICustomOptions & { description: string }) | undefined = useMemo(() => {
        const { testDrive } = equipment;

        if (!testDrive) {
            return;
        }

        const { duration, priceDuring, pricesAfter } = testDrive;

        const variants: IOptionVariant[] = orderBy(pricesAfter, ['type', 'duration'], ['desc', 'desc'])
            .map(variant => {
                const { id, type, duration: variantDuration, value } = variant;

                const isLeasing = type === EPriceTypes.Leasing;

                const uniqueTestDriveVariant = priceOptions.findIndex(({ id: optionId = 0 }) => optionId === id);

                if (uniqueTestDriveVariant < 0) {
                    priceOptions.push({
                        id,
                        label: getOptionLabel(type, variantDuration),
                        operType: variantDuration > 0 ? EPriceOperationTypes.Leasing : EPriceOperationTypes.Possess,
                        price: value,
                        type,
                        value: variantDuration,
                    });
                }

                return {
                    id: variantDuration,
                    label: getOptionLabel(type, variantDuration),
                    value,
                    valueStr: format(value, isLeasing ? { monthly: true } : undefined),
                };
            });

        return {
            id: EPriceOperationTypes.TestDrive,
            value: `${priceDuring} ${RubleSymbol} на ${duration} ${pluralizeDays(duration)}`,
            description: `Тестируйте оборудование ${duration} ${pluralizeDays(duration)}. Условия покупки оборудования после окончания тест-драйва:`,
            variants,
        };
    }, [equipment, priceOptions]);

    return { priceOptions, payOptions, testDriveOptions };
};
